import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DonutChart } from '@/components/ui/donut-chart';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart } from 'lucide-react';

interface CapitalDistributionData {
  name: string;
  value: number;
  count?: number;
}

interface CapitalDistributionChartProps {
  data: CapitalDistributionData[];
  isLoading: boolean;
  colors?: string[];
  title?: string;
  showValue?: boolean;
}

export const CapitalDistributionChart: React.FC<CapitalDistributionChartProps> = ({
  data,
  isLoading,
  colors = ['hsl(var(--primary))', 'hsl(202, 100%, 50%)', 'hsl(142, 76%, 36%)', 'hsl(38, 92%, 50%)'],
  title = "Capital Distribution",
  showValue = true
}) => {
  const valueFormatter = (value: number) => {
    if (title === "Capital Distribution") {
      return `$${value.toLocaleString()}`;
    }
    return value.toString();
  };

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const totalCount = data.reduce((sum, item) => sum + (item.count || 0), 0);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-64 flex items-center justify-center">
              <Skeleton className="h-48 w-48 rounded-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <PieChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No data available</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PieChart className="h-5 w-5" />
          {title}
        </CardTitle>
        {showValue && totalValue > 0 && (
          <div className="text-sm text-muted-foreground">
            Total: {valueFormatter(totalValue)}
            {totalCount > 0 && ` (${totalCount} accounts)`}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <DonutChart
            data={data}
            category="value"
            index="name"
            colors={colors}
            valueFormatter={valueFormatter}
            showLegend={true}
            className="h-full"
          />
        </div>
        
        {/* Detailed breakdown */}
        <div className="mt-4 space-y-2">
          {data.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span>{item.name}</span>
              </div>
              <div className="text-right">
                <div className="font-medium">{valueFormatter(item.value)}</div>
                {item.count !== undefined && (
                  <div className="text-muted-foreground">
                    {item.count} account{item.count !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};